/**
 * Care Type deconflation (product decision 2026-08-22).
 *
 * A service is not a business category: the four legacy care-type search slugs that describe
 * a *service* rather than a *facility category* are retired from the Care Type dropdown. Any
 * persisted value (URL params, saved searches, localStorage payloads, case→search navigation)
 * is translated to the required-services lane instead of dropped, so historical searches keep
 * working with correct semantics.
 *
 * The slugs stay valid in the taxonomy (registration/back-compat for stored provider rows
 * whose `business_type` column still contains them; see
 * `shared/taxonomy/care_taxonomy.json` `policy_care_class: "service_tag"`). Only the search
 * surface migrates them.
 *
 * Backend parity: `provider_query_filter_service.translate_service_like_business_type` applies
 * the identical translation server-side for direct API callers.
 */

import type { SearchFilters } from "../types/providerSearch";
import {
  BUSINESS_TYPE_DEFINITIONS,
  isServiceLikeSearchBusinessType,
} from "../generated/careTaxonomy";
import { resolveSearchBusinessTypeSlug } from "./caseTypeToBusinessType";

/**
 * Service-like business-type slug → `BUSINESS_TYPES` full label.
 *
 * Derived from generated `fullLabel`, the same string
 * `translate_service_like_business_type` reads. Not the care-services panel
 * label (`Respite Care / Short-Term Stay`) and not a community `service_key`.
 * Filter ingress may still display the panel label; the search request maps
 * that alias back to this catalog string.
 */
export const SERVICE_LIKE_CARE_TYPE_SERVICE_LABELS: Readonly<
  Record<string, string>
> = Object.fromEntries(
  BUSINESS_TYPE_DEFINITIONS.flatMap((definition) => {
    if (
      !isServiceLikeSearchBusinessType(definition.key) ||
      !definition.fullLabel
    ) {
      return [];
    }
    return [[definition.key, definition.fullLabel] as const];
  })
);

/**
 * True when a `business_type` filter value (slug or display label — any form that
 * `resolveSearchBusinessTypeSlug` recognizes) denotes a service, not a facility category.
 */
export function isServiceLikeCareTypeFilterValue(
  value: string | null | undefined
): boolean {
  const slug = resolveSearchBusinessTypeSlug(value);
  return slug !== undefined && isServiceLikeSearchBusinessType(slug);
}

/**
 * Translate a service-like `business_type` filter into the required-services lane.
 *
 * Returns the input unchanged when `business_type` is absent or is a real facility
 * category. When service-like: clears `business_type` and appends the mapped service label
 * to `required_core_services` (deduped, order-preserving), so the search enforces "must
 * provide this service" instead of the never-correct "must be this facility category".
 */
export function migrateServiceLikeCareTypeToServices(
  filters: SearchFilters
): SearchFilters {
  const businessType = filters.business_type?.trim();
  if (!businessType) {
    return filters;
  }

  const slug = resolveSearchBusinessTypeSlug(businessType);
  if (!slug || !isServiceLikeSearchBusinessType(slug)) {
    return filters;
  }

  const serviceLabel = SERVICE_LIKE_CARE_TYPE_SERVICE_LABELS[slug];
  if (!serviceLabel) {
    // Defensive: a slug classified service-like but unmapped should not silently vanish
    // from the identity lane. Leave it in place rather than degrade search intent.
    return filters;
  }

  const existing = filters.required_core_services ?? [];
  const alreadyPresent = existing.some(
    (label) => label.trim().toLowerCase() === serviceLabel.toLowerCase()
  );
  const nextServices = alreadyPresent
    ? existing
    : [...existing, serviceLabel];

  const migrated: SearchFilters = {
    ...filters,
    business_type: undefined,
    required_core_services: nextServices,
  };
  return migrated;
}
